import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

type TileDepth = "small" | "medium" | "large";

const DESKTOP_DEPTH_SPEED: Record<TileDepth, number> = {
  small: 0.2,
  medium: 0.32,
  large: 0.44,
};

const MOBILE_DEPTH_SPEED: Record<TileDepth, number> = {
  small: 0.2,
  medium: 0.36,
  large: 0.3,
};

function copyOffset(copy: HTMLElement) {
  const rect = copy.getBoundingClientRect();
  const offCenter = rect.top + rect.height / 2 - window.innerHeight / 2;
  const speed = window.matchMedia("(max-width: 900px)").matches ? 0.01 : 0.032;

  return offCenter * speed;
}

function tileOffset(tile: HTMLElement, depth: TileDepth) {
  const rect = tile.getBoundingClientRect();
  const offCenter = rect.top + rect.height / 2 - window.innerHeight / 2;
  const speed = window.matchMedia("(max-width: 900px)").matches
    ? MOBILE_DEPTH_SPEED[depth]
    : DESKTOP_DEPTH_SPEED[depth];

  return offCenter * speed;
}

/**
 * Mirrors the prototype's Chapter sequence: copy moves only subtly while the
 * image tiles travel at their authored depth rates. Entrance reveals are
 * handled by the shared UI component, so the CMS canvas and public site use
 * the same behavior. This stays in web because it is site-level parallax
 * orchestration.
 */
export function ChapterScrollEffects({ pathname }: { pathname: string }) {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reducedMotion.matches) return;

    gsap.registerPlugin(ScrollTrigger);
    const context = gsap.context(() => {
      const sections = gsap.utils.toArray<HTMLElement>(
        "[data-chapters-section]",
      );

      sections.forEach((section) => {
        const copy = section.querySelector<HTMLElement>("[data-chapters-copy]");
        const copyDrift = section.querySelector<HTMLElement>(
          "[data-chapters-copy-drift]",
        );
        const tiles = gsap.utils.toArray<HTMLElement>(
          section.querySelectorAll("[data-chapters-tile]"),
        );

        if (copy && copyDrift) {
          const updateCopy = () => gsap.set(copyDrift, { y: copyOffset(copy) });

          ScrollTrigger.create({
            trigger: section,
            start: "top bottom",
            end: "bottom top",
            onRefresh: updateCopy,
            onUpdate: updateCopy,
          });
        }

        tiles.forEach((tile) => {
          const drift = tile.querySelector<HTMLElement>(
            "[data-chapters-tile-drift]",
          );
          const depth = tile.dataset.chaptersTileDepth as TileDepth | undefined;

          if (!drift || !depth || !(depth in DESKTOP_DEPTH_SPEED)) return;

          const updateTile = () =>
            gsap.set(drift, { y: tileOffset(tile, depth) });

          ScrollTrigger.create({
            trigger: tile,
            start: "top bottom",
            end: "bottom top",
            onRefresh: updateTile,
            onUpdate: updateTile,
          });
        });
      });

      ScrollTrigger.refresh();
    });

    return () => context.revert();
  }, [pathname]);

  return null;
}
